import type { CurrentUserResponse } from "@/lib/session";

export interface ApiResult<T> {
  ok: boolean;
  status: number;
  data?: T;
  error?: string;
  /** True when the error is transient and the caller should retry */
  retryable?: boolean;
}

async function parseResponse<T>(response: Response): Promise<ApiResult<T>> {
  let payload: unknown;

  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }

  if (!response.ok) {
    const body = payload as { message?: string; error?: string; retryable?: boolean } | undefined;
    const message = body?.error ?? body?.message ?? `Request failed (${response.status})`;

    return {
      ok: false,
      status: response.status,
      error: message,
      retryable: body?.retryable === true || response.status === 503,
    };
  }

  return {
    ok: true,
    status: response.status,
    data: payload as T,
  };
}

export interface SignInPayload {
  email: string;
  password: string;
}

export async function signIn(payload: SignInPayload): Promise<ApiResult<CurrentUserResponse>> {
  const response = await fetch("/api/auth/sign-in", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  const result = await parseResponse<CurrentUserResponse & { accessToken?: string }>(response);

  if (result.ok && result.data?.accessToken) {
    try {
      localStorage.setItem("auth-token", result.data.accessToken);
    } catch { /* SSR / incognito guard */ }
  }

  return result;
}

export interface SignUpPayload {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  organizationName: string;
  countryCode?: string;

  // SaaS provisioning inputs (optional; server provides defaults)
  planId?: string;
  billingCycle?: "monthly" | "quarterly" | "biannual" | "annual";
  seatCount?: number;
  vehicleCount?: number;
  deviceCount?: number;
  enabledServices?: Record<string, boolean>;
  branding?: Record<string, unknown>;
  industry?: string;
  tenantPackage?: "core" | "insurance" | "farming";
  region?: string;
  website?: string;
  fleetSize?: string;
}

export async function signUp(payload: SignUpPayload): Promise<ApiResult<CurrentUserResponse>> {
  const response = await fetch("/api/auth/sign-up", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  const result = await parseResponse<CurrentUserResponse & { accessToken?: string }>(response);

  if (result.ok && result.data?.accessToken) {
    try {
      localStorage.setItem("auth-token", result.data.accessToken);
    } catch { /* SSR / incognito guard */ }
  }

  return result;
}

export async function signOut(): Promise<ApiResult<{ success: boolean }>> {
  try {
    localStorage.removeItem("auth-token");
    localStorage.removeItem("gt_session_cache");
  } catch { /* SSR / incognito guard */ }

  const response = await fetch("/api/auth/logout", {
    method: "POST",
    credentials: "include",
  });

  return parseResponse<{ success: boolean }>(response);
}

export interface ForgotPasswordPayload {
  email: string;
}

export async function requestPasswordReset(
  payload: ForgotPasswordPayload,
): Promise<ApiResult<{ success: boolean }>> {
  const response = await fetch("/api/auth/forgot-password", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  return parseResponse<{ success: boolean }>(response);
}

export interface ResetPasswordPayload {
  token: string;
  password: string;
}

export async function resetPassword(
  payload: ResetPasswordPayload,
): Promise<ApiResult<{ success: boolean }>> {
  const response = await fetch("/api/auth/reset-password", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  return parseResponse<{ success: boolean }>(response);
}